import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type StorageMode = 'local' | 'cloud';

const STORAGE_MODE_KEY = 'axis_storage_mode';
const MODE_SELECTED_KEY = 'axis_mode_selected';

export function useStorageMode() {
  const [mode, setModeState] = useState<StorageMode>(() => {
    const saved = localStorage.getItem(STORAGE_MODE_KEY);
    return (saved as StorageMode) || 'local';
  });

  const [modeSelected, setModeSelectedState] = useState<boolean>(() => {
    return localStorage.getItem(MODE_SELECTED_KEY) === 'true';
  });

  const [cloudUser, setCloudUser] = useState<any>(null);
  // Always check auth state to know if user can switch to cloud mode
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Sync mode across components/tabs via storage event
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_MODE_KEY && e.newValue) {
        setModeState(e.newValue as StorageMode);
      }
      if (e.key === MODE_SELECTED_KEY) {
        setModeSelectedState(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check auth state - we need this even in local mode to know if user can switch to cloud
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setCloudUser(session?.user ?? null);
        
        // If user logs out while in cloud mode, switch to local mode
        if (!session?.user && mode === 'cloud') {
          setMode('local');
        }
        
        setIsCheckingAuth(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setCloudUser(session?.user ?? null);
      setIsCheckingAuth(false);
    });

    return () => subscription.unsubscribe();
  }, [mode]);

  // Broadcast mode change to other components in the same tab
  const setMode = useCallback((newMode: StorageMode) => {
    localStorage.setItem(STORAGE_MODE_KEY, newMode);
    setModeState(newMode);
    // Dispatch a custom event for same-tab listeners
    window.dispatchEvent(new CustomEvent('storageModeChange', { detail: newMode }));
  }, []);

  // Listen for same-tab mode changes
  useEffect(() => {
    const handleModeChange = (e: Event) => {
      const customEvent = e as CustomEvent<StorageMode>;
      setModeState(customEvent.detail);
    };
    window.addEventListener('storageModeChange', handleModeChange);
    return () => window.removeEventListener('storageModeChange', handleModeChange);
  }, []);

  const setModeSelected = useCallback((selected: boolean) => {
    localStorage.setItem(MODE_SELECTED_KEY, String(selected));
    setModeSelectedState(selected);
  }, []);

  const isLocalMode = mode === 'local';
  const isCloudMode = mode === 'cloud';
  const isAuthenticated = !!cloudUser;

  // Can switch to cloud only if authenticated
  const canSwitchToCloud = isAuthenticated;

  return {
    mode,
    setMode,
    modeSelected,
    setModeSelected,
    isLocalMode,
    isCloudMode,
    isAuthenticated,
    cloudUser,
    canSwitchToCloud,
    isCheckingAuth,
  };
}

// Check if mode has been selected (for showing mode selection screen)
export function isModeSelected(): boolean {
  return localStorage.getItem(MODE_SELECTED_KEY) === 'true';
}

// Mark mode as selected
export function markModeSelected(): void {
  localStorage.setItem(MODE_SELECTED_KEY, 'true');
}

// Get current mode
export function getCurrentMode(): StorageMode {
  return (localStorage.getItem(STORAGE_MODE_KEY) as StorageMode) || 'local';
}
